import React, { useState, useEffect } from 'react';
import Wrapper from '../../Main/Universal/Wrapper';
import { postAPI, postAPIForm } from '../../../app/api';
import editIcon from "../../../theme/assets/images/icon/edit-icon.svg"
import deleteIcon from "../../../theme/assets/images/icon/delete-icon.svg"
import Swal from 'sweetalert2';

export default function Event() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        eventDate: '',
        eventTime: '',
        location: '',
        image: null,
        status: 'active'
    });

    // Fetch all events
    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const response = await postAPI('event/list', {});
            if (response?.status === 1) {
                setEvents(response?.data || []);
            }
        } catch (error) {
            console.error('Error fetching events:', error);
            Swal.fire('Error', 'Failed to fetch events', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle form changes
    const handleChange = (e) => {
        const { name, value, files } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: files ? files[0] : value
        }));
    };

    // Add/Update event
    const handleAddEvent = async (e) => {
        e.preventDefault();
        try {
            if (!formData.title || !formData.description || !formData.eventDate) {
                Swal.fire('Validation', 'Please fill all required fields', 'warning');
                return;
            }

            let submitData = {
                title: formData.title,
                description: formData.description,
                eventDate: formData.eventDate,
                eventTime: formData.eventTime,
                location: formData.location,
                status: formData.status
            };

            // If image exists, upload it
            if (formData.image && typeof formData.image !== 'string') {
                const fileFormData = new FormData();
                fileFormData.append('baseDir', 'events');
                fileFormData.append('filename', 'event_image');
                fileFormData.append('original_file', formData.image);
                
                const uploadResponse = await postAPIForm('file/uploadFile', fileFormData);
                if (uploadResponse?.status === 1) {
                    submitData.image = uploadResponse?.filepath;
                }
            }

            const response = await postAPI('event/create', submitData);
            if (response?.status === 1) {
                Swal.fire('Success', 'Event saved successfully', 'success');
                resetForm();
                fetchEvents();
            } else {
                Swal.fire('Error', response?.msg || 'Failed to save event', 'error');
            }
        } catch (error) {
            console.error('Error saving event:', error);
            Swal.fire('Error', error.message || 'Failed to save event', 'error');
        }
    };

    // Delete event
    const handleDeleteEvent = async (id) => {
        const result = await Swal.fire({
            title: 'Confirm Delete',
            text: 'Are you sure you want to delete this event?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('event/delete', { id });
                if (response?.status === 1) {
                    Swal.fire('Success', 'Event deleted successfully', 'success');
                    fetchEvents();
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to delete event', 'error');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            eventDate: '',
            eventTime: '',
            location: '',
            image: null,
            status: 'active'
        });
        setShowForm(false);
        setEditingId(null);
    };

    const handleToggleForm = () => {
        if (showForm) {
            // If form is shown, close it and reset
            setShowForm(false);
            resetForm();
        } else {
            // If form is hidden, show it with fresh data
            setEditingId(null);
            setFormData({
                title: '',
                description: '',
                eventDate: '',
                eventTime: '',
                location: '',
                image: null,
                status: 'active'
            });
            setShowForm(true);
        }
    };

    return (
        <>
            <Wrapper>
                <div className="container-fluid">
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center">
                                <h3 className="mb-0">News & Events Management</h3>
                                <button 
                                    className="btn btn-primary"
                                    onClick={handleToggleForm}
                                >
                                    {showForm ? 'Cancel' : 'Add New Event'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {showForm && (
                        <div className="card mb-4">
                            <div className="card-body">
                                <h5 className="card-title">{editingId ? 'Edit Event' : 'Add New Event'}</h5>
                                <form onSubmit={handleAddEvent}>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Title *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="title"
                                                value={formData.title}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Location</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="location"
                                                value={formData.location}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Event Date *</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="eventDate"
                                                value={formData.eventDate}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Event Time</label>
                                            <input
                                                type="time"
                                                className="form-control"
                                                name="eventTime"
                                                value={formData.eventTime}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        {/* <div className="col-md-6 mb-3">
                                            <label className="form-label">Status</label>
                                            <select
                                                className="form-control"
                                                name="status"
                                                value={formData.status}
                                                onChange={handleChange}
                                            >
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </select>
                                        </div> */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Event Image</label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                name="image"
                                                onChange={handleChange}
                                                accept="image/*"
                                            />
                                        </div>
                                        <div className="col-12 mb-3">
                                            <label className="form-label">Description *</label>
                                            <textarea
                                                className="form-control"
                                                name="description"
                                                value={formData.description}
                                                onChange={handleChange}
                                                rows="3"
                                                required
                                            ></textarea>
                                        </div>
                                    </div>
                                    <button type="submit" className="btn btn-success">
                                        {editingId ? 'Update' : 'Add'} Event
                                    </button>
                                </form>
                            </div>
                        </div>
                    )}

                    <div className="card">
                        <div className="card-body">
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-striped table-hover cus-table">
                                        <thead className="table-dark">
                                            <tr>
                                                <th>Title</th>
                                                <th>Date</th>
                                                <th>Location</th>
                                                <th>Description</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {events && events.length > 0 ? (
                                                events.map((event, index) => (
                                                    <tr key={index}>
                                                        <td>{event.title}</td>
                                                        <td>{event.eventDate}</td>
                                                        <td>{event.location}</td>
                                                        <td>{event.description?.substring(0, 30)}...</td>
                                                        <td>
                                                            <span className={`badge badge-${event.status === 1 ? 'success' : 'danger'}`}>
                                                                {event.status === 1 ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="btn me-2 py-0 my-0 mx-0"
                                                                onClick={() => {
                                                                    setFormData(event);
                                                                    setEditingId(event.id);
                                                                    setShowForm(true);
                                                                }}
                                                            >
                                                                <img src={editIcon} />
                                                            </button>
                                                            <button
                                                                className="btn py-0 my-0 mx-0"
                                                                onClick={() => handleDeleteEvent(event.id)}
                                                            >
                                                                <img src={deleteIcon} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-4">
                                                        No events found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Wrapper>
        </>
    );
}
